/**
 * Pairwise distance heatmap for the first m particles (Yang-style lens panel).
 */

export class DistanceMatrixView {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private m = 12;

  constructor(host: HTMLElement) {
    host.innerHTML = "";
    const wrap = document.createElement("div");
    wrap.className = "distance-matrix-wrap";
    this.canvas = document.createElement("canvas");
    this.canvas.width = 120;
    this.canvas.height = 120;
    this.canvas.setAttribute("aria-label", "Pairwise distance matrix");
    wrap.appendChild(this.canvas);
    const cap = document.createElement("p");
    cap.className = "field-help-summary";
    cap.style.display = "block";
    cap.textContent = "First m particles · darker = closer";
    wrap.appendChild(cap);
    host.appendChild(wrap);
    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("2d context");
    this.ctx = ctx;
  }

  setSize(m: number): void {
    this.m = Math.max(2, Math.min(32, m | 0));
  }

  draw(positions: Float64Array, dim: number, n: number): void {
    const m = Math.min(this.m, n);
    const cell = 8;
    const px = m * cell;
    if (this.canvas.width !== px) {
      this.canvas.width = px;
      this.canvas.height = px;
    }
    const { ctx } = this;
    // Find max pairwise for normalize
    let maxD = 1e-9;
    const dist = new Float64Array(m * m);
    for (let i = 0; i < m; i++) {
      for (let j = 0; j < m; j++) {
        if (i === j) {
          dist[i * m + j] = 0;
          continue;
        }
        let s = 0;
        for (let d = 0; d < dim; d++) {
          const a = positions[i * dim + d]! - positions[j * dim + d]!;
          s += a * a;
        }
        const d = Math.sqrt(s);
        dist[i * m + j] = d;
        if (d > maxD) maxD = d;
      }
    }
    for (let i = 0; i < m; i++) {
      for (let j = 0; j < m; j++) {
        const t = dist[i * m + j]! / maxD;
        // dark = close (Yang spirit)
        const v = Math.floor(20 + t * 200);
        ctx.fillStyle = `rgb(${v},${Math.floor(v * 0.85)},${Math.floor(40 + t * 180)})`;
        ctx.fillRect(j * cell, i * cell, cell, cell);
      }
    }
  }

  clear(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
